import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
} from '@mui/material';
import { ReactElement, useState } from 'react';
import { Doc } from 'src/@types/docs';
import { useConfirmationModal } from 'src/components/ConfirmationModal';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useDocsUIState } from 'src/state/ui/useDocsUIState';
import { useDocAllowedAction } from '../../../shared/useDocAllowedAction';
import { useDocDuplicateMutation } from '../../../shared/useDocDuplicateMutation';
import { useDocExportMutation } from '../../../shared/useDocExportMutation';
import { useDocFavoriteMutation } from '../../../shared/useDocFavoritedMutation';
import { useUpdateDocMutation } from '../../../shared/useUpdateDocMutation';
import { useDocShareDialog } from '../DocShareDialog';
import { DocFavoriteIcon } from './FavoriteDocButton';
import { DocPinIcon } from './PinDocButton';
import { ShowDocFooterIcon } from './ShowDocFooterIcon';
import { useDocHeaderActions } from './useDocHeaderActions';

type Props = {
  doc: Doc;
  handlePrint: () => void;
};

export const DocOptionsMenu = ({ doc, handlePrint }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    handleCreateDoc,
    handleDuplicate,
    handleFavorite,
    handleLeaveDoc,
    handleExport,
    handleDeleteDoc,
    handleToogleShowFooter,
    handleSwitchChange,
    toggleDocOnActionBar,
    isOnActionBar,
  } = useDocHeaderActions({ doc });

  const userPermissions = useDocAllowedAction({ doc });

  const { translate } = useLocales();

  const { isFavoritingDoc, isDisfavoringDoc } = useDocFavoriteMutation();
  const { isUpdateDocLoading } = useUpdateDocMutation();

  const { docsFullWidth } = useDocsUIState(doc._id);

  const { showDocShareDialog } = useDocShareDialog();

  const { isDuplicatingDoc } = useDocDuplicateMutation();
  const { isExportingDoc } = useDocExportMutation();

  const { confirm } = useConfirmationModal();

  const OPTIONS: {
    label: string;
    icon: ReactElement;
    onClick?: () => Promise<void> | void;
    loading?: boolean;
    allowedAction: boolean;
  }[] = [
    {
      label: translate('collaborate.docs.header.new_document'),
      icon: <Iconify icon="eva:plus-fill" />,
      async onClick() {
        await handleCreateDoc();
      },
      allowedAction: userPermissions.canCreateSubDoc,
    },
    {
      label: translate('shared.buttons.share'),
      icon: <Iconify icon="tabler:share-3" />,
      onClick: () => showDocShareDialog({ docId: doc._id }),
      allowedAction: userPermissions.canShareDoc,
    },
    {
      label: translate('shared.buttons.print'),
      icon: <Iconify icon="tabler:printer" />,
      onClick: handlePrint,
      allowedAction: true,
    },
    {
      label: translate('shared.buttons.duplicate'),
      icon: <Iconify icon="tabler:copy" />,
      onClick: handleDuplicate,
      loading: isDuplicatingDoc,
      allowedAction: userPermissions.canDuplicate,
    },
    {
      label: translate('shared.buttons.export'),
      icon: <Iconify icon="tabler:download" />,
      onClick: handleExport,
      loading: isExportingDoc,
      allowedAction: userPermissions.canDuplicate,
    },
    {
      label: isOnActionBar
        ? translate('collaborate.docs.remove_from_actions_bar')
        : translate('collaborate.docs.add_to_actions_bar'),
      icon: <DocPinIcon isOnActionBar={isOnActionBar} />,
      onClick: toggleDocOnActionBar,
      allowedAction: true,
    },
    {
      label: translate('shared.buttons.favorite'),
      icon: <DocFavoriteIcon starred={doc.starred} />,
      onClick: handleFavorite,
      loading: isFavoritingDoc || isDisfavoringDoc,
      allowedAction: true,
    },
    {
      label: doc.showFooter
        ? translate('collaborate.docs.hide_footer')
        : translate('collaborate.docs.show_footer'),
      icon: <ShowDocFooterIcon showFooter={doc.showFooter} />,
      onClick: handleToogleShowFooter,
      loading: isUpdateDocLoading,
      allowedAction: userPermissions.canToogleFooter,
    },
  ];

  const onDeleteDoc = async () => {
    if (
      !(await confirm({
        title: translate('shared.confirmation.are_you_sure_title'),
        message: doc.public
          ? translate(
              'shared.confirmation.are_you_sure_move_public_doc_to_trash',
              {
                title: doc.title,
              },
            )
          : translate('shared.confirmation.are_you_sure_move_to_trash', {
              title: doc.title,
            }),
      }))
    )
      return;
    await handleDeleteDoc();
    handleClose();
  };

  const onLeaveDoc = async () => {
    if (
      !(await confirm({
        title: translate('shared.confirmation.are_you_sure_title'),
        message: translate(
          'shared.confirmation.are_you_sure_want_to_leave_doc',
        ),
      }))
    )
      return;
    await handleLeaveDoc();
    handleClose();
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <Iconify icon="tabler:dots-vertical" />
      </IconButton>

      <Menu
        id="header-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {OPTIONS.filter((option) => option.allowedAction).map(
          (option, index) => (
            <MenuItem
              onClick={() => {
                option.onClick?.();
                handleClose();
              }}
              key={`folder-dropdown-${index}`}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText primary={option.label} />
            </MenuItem>
          ),
        )}

        <MenuItem key="folder-dropdown-4">
          <ListItemText primary={translate('collaborate.docs.full_width')} />
          <Switch
            variant="custom"
            checked={docsFullWidth}
            onChange={handleSwitchChange}
          />
        </MenuItem>
        <Divider />

        <MenuItem
          key="folder-dropdown-5"
          onClick={userPermissions.canDelete ? onDeleteDoc : onLeaveDoc}
        >
          <ListItemIcon>
            <Iconify icon="ph:trash" />
          </ListItemIcon>
          <ListItemText
            primary={
              userPermissions.canDelete
                ? translate('collaborate.docs.delete_document')
                : translate('collaborate.docs.leave_document')
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
};
